/**
 * Tool rail of the Chart workspace (W2-03 note-lane; DESIGN.md §4.5 "Tools rail (48 px; 120 px with labels in
 * Guided): Select (V), Tap (1), Drag (2), Flick (3), Hold (4), Eraser (X), Generators").
 *
 * A vertical toolbar (roving tab stop, arrows move): one toggle per tool (pressed = current tool, tooltip with the
 * shortcut), and the Generators menu (Distribute, Clone to lines, Fill between, Motion path, Motion templates), which
 * opens the generator sheets and the templates dialog of their packages through the dialog registry and contracted
 * commands. Guided shows labels next to the icons. Buttons never take focus on pointer down (Space keeps playing).
 */

import type { AppServices, PanelProps } from '../../app/registry/types';
import { DIALOG } from '../../app/dialogs/types';
import { useT } from '../../i18n/useT';
import type { MessageKey } from '../../i18n/types';
import type { ToolId } from '../../state/types';
import { Button } from '../../ui/Button';
import { IconButton } from '../../ui/IconButton';
import { Menu } from '../../ui/Menu';
import { Toolbar, ToolbarSeparator } from '../../ui/Toolbar';
import type { MenuItem } from '../../ui/types';
import { useEditorSelect } from './hooks';
import { m } from './noteLane.messages';
import { activeLineOf, laneSession, playheadBeat, ref, selectedItems, snapBeatAt } from './laneSession';
import s from './ToolRail.module.css';

export interface ToolSpec {
  readonly tool: ToolId;
  readonly command: string;
  readonly icon: string;
  readonly title: MessageKey;
  readonly short: MessageKey;
}

export const TOOLS: readonly ToolSpec[] = [
  { tool: 'select', command: 'tools.select', icon: 'MousePointer2', title: m.toolSelect, short: m.toolSelectShort },
  { tool: 'tap', command: 'tools.tap', icon: 'RectangleHorizontal', title: m.toolTap, short: m.toolTapShort },
  { tool: 'drag', command: 'tools.drag', icon: 'Minus', title: m.toolDrag, short: m.toolDragShort },
  { tool: 'flick', command: 'tools.flick', icon: 'ArrowBigUp', title: m.toolFlick, short: m.toolFlickShort },
  { tool: 'hold', command: 'tools.hold', icon: 'RectangleVertical', title: m.toolHold, short: m.toolHoldShort },
  { tool: 'eraser', command: 'tools.eraser', icon: 'Eraser', title: m.toolEraser, short: m.toolEraserShort },
];

/** Picks a tool (also the sticky note kind for note tools). */
export function pickTool(services: AppServices, tool: ToolId): void {
  const e = services.editor.getState();
  e.setTool(tool);
  if (tool === 'tap' || tool === 'drag' || tool === 'flick' || tool === 'hold') e.setPlaceKind(tool);
  laneSession(services).redraw(['overlay']);
}

/** Generator menu items: the sheets of W2-11 and the templates dialog of W2-05. */
export function generatorItems(services: AppServices, t: (key: string) => string): MenuItem[] {
  const sheet = (id: typeof DIALOG.clone | typeof DIALOG.fill | typeof DIALOG.motionPath): void => {
    const sel = selectedItems(services);
    void services.ui.openDialog(id, { notes: sel.notes, segments: sel.segments, line: activeLineOf(services) });
  };
  const noLine = activeLineOf(services) === null ? ref(m.genNeedsLine) : null;
  return [
    {
      kind: 'action',
      label: t(m.genDistribute),
      icon: 'ChartNoAxesColumnIncreasing',
      onSelect: () => {
        void services.registry.run('tools.distribute', undefined);
      },
    },
    {
      kind: 'action',
      label: t(m.genClone),
      icon: 'Copy',
      onSelect: () => {
        sheet(DIALOG.clone);
      },
    },
    {
      kind: 'action',
      label: t(m.genFill),
      icon: 'Rows3',
      onSelect: () => {
        sheet(DIALOG.fill);
      },
    },
    {
      kind: 'action',
      label: t(m.genPath),
      icon: 'Spline',
      disabledReason: noLine,
      onSelect: () => {
        sheet(DIALOG.motionPath);
      },
    },
    { kind: 'separator' },
    {
      kind: 'action',
      label: t(m.genTemplates),
      icon: 'LayoutTemplate',
      disabledReason: noLine,
      onSelect: () => {
        const line = activeLineOf(services);
        if (line === null) return;
        const at = snapBeatAt(playheadBeat(services), services.editor.getState().snap, false);
        // One tool sheet at a time (UX-038): the generator sheets close (their previews revert) first.
        for (const id of [DIALOG.distribute, DIALOG.clone, DIALOG.fill, DIALOG.motionPath, DIALOG.bake, DIALOG.flatten])
          services.ui.closeDialog(id);
        void services.ui.openDialog(DIALOG.templates, { line, at });
      },
    },
  ];
}

export function ToolRail(props: PanelProps) {
  const { services } = props;
  const t = useT();
  const tool = useEditorSelect(services, (st) => st.tool);
  const guided = useEditorSelect(services, (st) => st.level === 'guided');
  return (
    <div className={guided ? `${s.rail} ${s.labelled}` : s.rail} data-chrome="" data-help-id="tool-rail">
      <Toolbar label={ref(m.toolsLabel)} orientation="vertical" className={s.toolbar}>
        {TOOLS.map((spec) =>
          guided ? (
            <Button
              key={spec.tool}
              icon={spec.icon}
              command={spec.command}
              variant={tool === spec.tool ? 'primary' : 'ghost'}
              size="sm"
              aria-pressed={tool === spec.tool}
              data-tool={spec.tool}
              helpId={`tool-${spec.tool}`}
              className={s.labelButton}
              onClick={() => {
                pickTool(services, spec.tool);
              }}
            >
              {t(spec.short)}
            </Button>
          ) : (
            <IconButton
              key={spec.tool}
              icon={spec.icon}
              label={ref(spec.title)}
              command={spec.command}
              pressed={tool === spec.tool}
              tooltipSide="right"
              data-tool={spec.tool}
              helpId={`tool-${spec.tool}`}
              className={tool === spec.tool ? s.active : undefined}
              onClick={() => {
                pickTool(services, spec.tool);
              }}
            />
          ),
        )}
        <ToolbarSeparator />
        <Menu
          side="right"
          label={t(m.generators)}
          trigger={
            guided ? (
              <Button icon="WandSparkles" variant="ghost" size="sm" className={s.labelButton}>
                {t(m.generatorsShort)}
              </Button>
            ) : (
              <IconButton icon="WandSparkles" label={ref(m.generators)} tooltipSide="right" />
            )
          }
          items={generatorItems(services, t)}
        />
      </Toolbar>
    </div>
  );
}
